/**
 * <ScoutDiscover>
 *
 * The conversational AI product-discovery widget used in three places:
 *   • Embedded on the homepage hero
 *   • As the body of the /discover page
 *   • Inside the slide-in panel triggered by <ScoutFloatingButton>
 *
 * All three share state-free, prop-driven rendering — no React Context,
 * no global store. If a parent wants the input pre-filled or auto-
 * submitted (e.g., chip click), it controls the `initialQuery` prop.
 *
 * Talks to POST /api/discover/recommend, renders side-by-side product
 * cards with pros/cons, and surfaces clarifying questions or off-topic
 * redirects when the LLM emits them.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Sparkles, ArrowRight, CheckCircle2, AlertCircle } from 'lucide-react';
import ResilientImg from './ResilientImg';

const API = process.env.REACT_APP_BACKEND_URL;

const SUGGESTED_CHIPS = [
  'Save time on emails',
  'Get more clients from cold outreach',
  'Automate my social posts',
  'Write proposals faster',
  'Build a podcast workflow',
  'Onboard new clients without me typing',
];

function _getSessionId() {
  let s = sessionStorage.getItem('scout_sid');
  if (!s) {
    s = 'scout-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
    sessionStorage.setItem('scout_sid', s);
  }
  return s;
}

export default function ScoutDiscover({
  initialQuery = '',
  autoFocus = false,
  variant = 'page', // 'page' | 'hero' | 'panel'
}) {
  const [query, setQuery] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) inputRef.current.focus();
  }, [autoFocus]);

  useEffect(() => {
    // Auto-submit if a parent handed us a non-empty initialQuery.
    if (initialQuery && initialQuery.trim()) {
      submit(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  async function submit(q) {
    const trimmed = (q || '').trim();
    if (!trimmed || loading) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/discover/recommend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: trimmed, session_id: _getSessionId() }),
      });
      if (!res.ok) {
        if (res.status === 429) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || "You've hit Scout's free query limit. Try again shortly.");
        }
        throw new Error('Scout is having a moment — please try again.');
      }
      const data = await res.json();
      setResult(data);
    } catch (e) {
      setError(e.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    submit(query);
  }

  const isHero = variant === 'hero';
  const isPanel = variant === 'panel';

  return (
    <div
      data-testid="scout-discover-root"
      className={`w-full ${isPanel ? '' : 'max-w-5xl mx-auto'}`}
    >
      {/* === INPUT === */}
      <form
        onSubmit={handleSubmit}
        data-testid="scout-form"
        className={`relative ${isPanel ? 'mb-4' : 'mb-5'}`}
      >
        <div className="relative">
          <Sparkles className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-cyan-400 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="What can I help you find?"
            data-testid="scout-input"
            disabled={loading}
            className="w-full pl-12 pr-32 py-4 bg-neutral-900/80 border border-white/10 rounded-2xl text-white placeholder:text-neutral-500 focus:outline-none focus:border-cyan-400/60 focus:bg-neutral-900 transition-colors text-base"
          />
          <button
            type="submit"
            disabled={loading || !query.trim()}
            data-testid="scout-submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 bg-cyan-400 text-black font-semibold px-5 py-2 rounded-xl hover:bg-cyan-300 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5 transition-colors"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Ask Scout <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>

        {/* === SUGGESTED CHIPS === */}
        {!result && !loading && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="scout-chips">
            {SUGGESTED_CHIPS.slice(0, isPanel ? 3 : 6).map((chip) => (
              <button
                key={chip}
                type="button"
                onClick={() => {
                  setQuery(chip);
                  submit(chip);
                }}
                data-testid={`scout-chip-${chip.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}
                className="text-xs px-3 py-1.5 rounded-full border border-white/10 text-neutral-300 hover:border-cyan-400/40 hover:text-cyan-300 transition-colors"
              >
                {chip}
              </button>
            ))}
          </div>
        )}
      </form>

      {/* === ERROR === */}
      {error && (
        <div
          data-testid="scout-error"
          className="bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl px-4 py-3 text-sm flex items-start gap-2"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* === LOADING === */}
      {loading && (
        <div
          data-testid="scout-loading"
          className="text-center py-12 text-neutral-400 text-sm"
        >
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-3 text-cyan-400" />
          Scout is reading the catalog…
        </div>
      )}

      {/* === RESULT === */}
      {result && (
        <ScoutResult result={result} isPanel={isPanel} onAskAgain={() => setResult(null)} />
      )}
    </div>
  );
}

function ScoutResult({ result, isPanel, onAskAgain }) {
  if (result.off_topic_reply) {
    return (
      <div
        data-testid="scout-off-topic"
        className="bg-neutral-900/60 border border-white/10 rounded-2xl p-5 text-neutral-300 text-sm leading-relaxed"
      >
        {result.off_topic_reply}
        <button
          type="button"
          onClick={onAskAgain}
          className="block mt-4 text-cyan-400 hover:text-cyan-300 text-sm font-medium"
        >
          Ask Scout something else →
        </button>
      </div>
    );
  }

  if (result.clarifying_question) {
    return (
      <div
        data-testid="scout-clarify"
        className="bg-cyan-400/5 border border-cyan-400/20 rounded-2xl p-5 text-neutral-200 text-sm leading-relaxed"
      >
        <span className="text-cyan-400 font-semibold">Scout: </span>
        {result.clarifying_question}
        <button
          type="button"
          onClick={onAskAgain}
          className="block mt-3 text-cyan-400 hover:text-cyan-300 text-sm font-medium"
        >
          Try a different question →
        </button>
      </div>
    );
  }

  const recs = result.recommendations || [];
  if (recs.length === 0) {
    return (
      <div
        data-testid="scout-no-results"
        className="bg-neutral-900/60 border border-white/10 rounded-2xl p-5 text-neutral-300 text-sm"
      >
        Scout couldn't find a great match. Try rephrasing — describe the
        outcome you want, not the tool you have in mind.
        <button
          type="button"
          onClick={onAskAgain}
          className="block mt-3 text-cyan-400 hover:text-cyan-300 text-sm font-medium"
        >
          Ask again →
        </button>
      </div>
    );
  }

  return (
    <div
      data-testid="scout-results"
      className={`grid gap-4 ${recs.length === 1 ? 'grid-cols-1' : recs.length === 2 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 lg:grid-cols-3'} ${isPanel ? '' : 'mt-2'}`}
    >
      {recs.map((rec) => (
        <ScoutCard key={rec.slug} rec={rec} isPanel={isPanel} />
      ))}
      <button
        type="button"
        onClick={onAskAgain}
        data-testid="scout-ask-again"
        className={`${recs.length === 1 ? 'col-span-1' : recs.length === 2 ? 'lg:col-span-2' : 'lg:col-span-3'} mt-2 text-center text-cyan-400 hover:text-cyan-300 text-sm font-medium`}
      >
        Ask Scout something else →
      </button>
    </div>
  );
}

function ScoutCard({ rec, isPanel }) {
  const isPartner = rec.kind === 'partner';
  return (
    <div
      data-testid={`scout-rec-${rec.slug}`}
      className="bg-neutral-900/60 border border-white/10 rounded-2xl overflow-hidden hover:border-cyan-400/40 transition-colors flex flex-col"
    >
      <div className="relative aspect-video bg-neutral-950 overflow-hidden border-b border-white/5">
        {rec.image_url ? (
          <ResilientImg
            src={
              rec.image_url.startsWith('/')
                ? `${API}${rec.image_url}`
                : rec.image_url
            }
            alt={rec.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-neutral-700 text-3xl font-bold">
            {(rec.name || '?')[0].toUpperCase()}
          </div>
        )}
        {isPartner && (
          <span
            data-testid="scout-rec-partner-tag"
            className="absolute top-2 left-2 bg-black/70 backdrop-blur text-cyan-300 text-[10px] font-bold tracking-wider uppercase px-2 py-1 rounded"
          >
            Partner tool
          </span>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-white font-semibold text-base leading-tight">{rec.name}</h3>
          {rec.price_label && (
            <span className="text-cyan-300 text-sm font-semibold flex-shrink-0">
              {rec.price_label}
            </span>
          )}
        </div>
        {rec.tagline && (
          <p className="text-neutral-400 text-xs mb-3 line-clamp-2">{rec.tagline}</p>
        )}

        {rec.why_match && (
          <p className="text-neutral-200 text-sm mb-3 leading-relaxed">
            <span className="text-cyan-400 font-semibold">Why: </span>
            {rec.why_match}
          </p>
        )}

        {(rec.pros || []).length > 0 && (
          <ul className="space-y-1 mb-2">
            {rec.pros.map((pro, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-neutral-300">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span>{pro}</span>
              </li>
            ))}
          </ul>
        )}
        {(rec.cons || []).length > 0 && (
          <ul className="space-y-1 mb-3">
            {rec.cons.map((con, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-neutral-400">
                <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
                <span>{con}</span>
              </li>
            ))}
          </ul>
        )}

        <a
          href={rec.url}
          target={isPartner ? '_blank' : undefined}
          rel={isPartner ? 'noopener noreferrer' : undefined}
          data-testid={`scout-rec-cta-${rec.slug}`}
          className="mt-auto bg-cyan-400 text-black font-semibold text-sm px-4 py-2.5 rounded-lg hover:bg-cyan-300 transition-colors text-center"
        >
          {isPartner ? 'Visit partner site →' : 'View product →'}
        </a>
      </div>
    </div>
  );
}
